
import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, doc, updateDoc, getDoc, arrayUnion } from 'firebase/firestore';
import moment from 'moment';
import { db } from '../lib/firebase';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Transfer.css';
import Loader from '../components/Loader';
import {
    FaUserCircle
} from "react-icons/fa"


const AddTransaction = () => {
    const BRAND_NAME = process.env.REACT_APP_BRAND_NAME;

    // State variables
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [amount, setAmount] = useState(0);
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [type, setType] = useState('credit');
    const [account, setAccount] = useState('Savings');
    const [userBalance, setUserBalance] = useState(0);
    const [accountBalance, setAccountBalance] = useState(0);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [userInput, setUserInput] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [currentUserRole, setCurrentUserRole] = useState(null)
    const [authLoading, setAuthLoading] = useState(true)


    useEffect(() => {
        const fetchCurrentUserRole = async () => {
            const auth = getAuth()
            const currentUser = auth.currentUser

            if (!currentUser) {
                setCurrentUserRole("unauthenticated")
                setAuthLoading(false)
                return
            }

            try {
                const currentUserDoc = await getDoc(doc(db, "users", currentUser.uid))
                if (currentUserDoc.exists()) {
                    const role = currentUserDoc.data().role || "user"
                    setCurrentUserRole(role)
                } else {
                    setCurrentUserRole("unknown")
                }
            } catch (error) {
                console.error("Error fetching current user role:", error)
                setCurrentUserRole("error")
            } finally {
                setAuthLoading(false)
            }
        }

        fetchCurrentUserRole()
    }, [])



    // * Fetch users from Firestore on component mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'users'));
                const usersList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUsers(usersList);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching users: ', error);
                setErrorMessage('Error fetching users.');
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // * Handle user change in the dropdown
    const handleUserChange = async (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const fetchedTransactions = userDoc.data().transactions || [];
                const calculatedBalance = fetchedTransactions.reduce((total, transaction) => {
                    // if transaction type is credit, add amount else subtract
                    return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                }, 0);
                setUserBalance(calculatedBalance);

                const accountTransactions = fetchedTransactions.filter(txn => txn.account === account);
                const calculatedAccountBalance = accountTransactions.reduce((total, transaction) => {
                    return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                }, 0);
                setAccountBalance(calculatedAccountBalance);
            }
        } catch (error) {
            console.error('Error fetching user balance: ', error);
            setErrorMessage('Error fetching user balance.');
        }
    };

    // * Handle account change
    const handleAccountChange = (e) => {
        setAccount(e.target.value);
        if (selectedUserId) {
            // Recalculate balance for the new account type
            handleUserChange({ target: { value: selectedUserId } });
        }
    };

    // * Handle all changes from user input
    const handleUserInputChange = (e) => {
        const input = e.target.value;
        setUserInput(input);

        if (input.trim() === '') {
            setFilteredUsers([]);
        } else {
            const filtered = users.filter(user =>
                user.fullName && user.fullName.toLowerCase().includes(input.toLowerCase()) // Check if fullName exists
            );
            setFilteredUsers(filtered);
        }
    };


    // * Handle the user selection
    const handleUserSelect = async (userId) => {
        setSelectedUserId(userId);
        setUserInput('');
        setFilteredUsers([]);
        try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const transactions = userDoc.data().transactions || [];
                const calculatedBalance = transactions.reduce((total, txn) => {
                    return txn.type === 'credit' ? total + txn.amount : total - txn.amount;
                }, 0);
                setUserBalance(calculatedBalance);
            }
        } catch (error) {
            console.error('Error fetching user balance: ', error);
            setErrorMessage('Error fetching user balance.');
        }
    };

    // *go back buttons
    const handleGoBack = () => {
        navigate(-1);
    };

    // * Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Start submission state
        setFormSubmitting(true);
        // Clear previous errors
        setErrorMessage('');
        // Clear previous success messages
        setSuccessMessage('');

        // Validation
        if (!selectedUserId || !amount || !description || !date || !type || !account) {
            setErrorMessage('All fields are required.');
            setFormSubmitting(false);
            return;
        }

        const parsedAmount = parseFloat(amount);
        if (parsedAmount <= 0) {
            setErrorMessage('Amount must be greater than 0.');
            setFormSubmitting(false);
            return;
        }

        if (type === 'debit' && userBalance < parsedAmount) {
            setErrorMessage('Insufficient balance for a debit transaction.');
            setFormSubmitting(false);
            return;
        }

        try {
            // Create the transaction object
            const transaction = {
                amount: parsedAmount,
                description,
                // Human-readable format
                date: moment(date).format('MMMM D, YYYY'),
                type,
                beneficiary: BRAND_NAME,
                account,
            };

            // Update the user's transactions in Firestore
            await updateDoc(doc(db, 'users', selectedUserId), {
                transactions: arrayUnion(transaction),
            });

            // Update local state for balance
            const newBalance = type === 'credit' ? userBalance + parsedAmount : userBalance - parsedAmount;
            setUserBalance(newBalance);

            const newAccountBalance = type === 'credit' ? accountBalance + parsedAmount : accountBalance - parsedAmount;
            setAccountBalance(newAccountBalance);

            // Reset form fields
            setAmount(0);
            setDescription('');
            // Reset to today's date
            setDate(moment().format('YYYY-MM-DD'));
            setType('credit');

            // Display success message
            setSuccessMessage('Transaction added successfully! Click here to view the transaction.');
        } catch (error) {
            console.error('Error adding transaction:', error);
            setErrorMessage('Error adding transaction. Please try again.');
        } finally {
            // End submission state
            setFormSubmitting(false);
        }
    };


    if (loading) {
        return <div><Loader /></div>;
    }

    if (errorMessage) {
        return <div>{errorMessage}</div>;
    }

        if (authLoading || loading) {
        return <Loader />
    }

    if (currentUserRole !== "admin") {
        return (
            <div className="add-transaction-admin-denied">
                <FaUserCircle size={48} />
                <h2>Access Denied</h2>
                <p>You must be an admin to access this page.</p>
                <div onClick={handleGoBack} className="add-transaction-back-btn">
                    Go Back
                </div>
            </div>
        )
    }

    return (
        <div className="add-transaction-page">
            <div className="add-transaction">
                <div className="add-transaction-header">
                    <h2>Add Transaction</h2>
                    <button onClick={handleGoBack} className="add-transaction-back-btn">
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                </div>

                <div className="add-transaction-selected-user">
                    {selectedUserId ? (
                        <div className="selected-user-card">
                            <div className="selected-user-info-box">
                                <span className="selected-user-name">{users.find(user => user.id === selectedUserId)?.fullName}</span>
                                <span className="selected-user-email">{users.find(user => user.id === selectedUserId)?.email}</span>
                                <span className="selected-user-balance">Balance: ${userBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                        </div>
                    ) : (
                        <p className="no-user-selected">Search and select a user above</p>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="add-transaction-form">
                    <div className="form-section">
                        <div className="form-group">
                            <label className="transaction-head">Search User:</label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder="Type to search users..."
                                value={userInput}
                                onChange={handleUserInputChange}
                            />
                            {filteredUsers.length > 0 && (
                                <ul className="user-dropdown">
                                    {filteredUsers.map(user => (
                                        <li
                                            key={user.id}
                                            onClick={() => handleUserSelect(user.id)}
                                            className="user-item"
                                        >
                                            <div className="user-item-info">
                                                <span className="user-item-name">{user.fullName}</span>
                                                <span className="user-item-email">{user.email}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="transaction-head">Type:</label>
                                <select
                                    value={type}
                                    className="input-field"
                                    onChange={(e) => setType(e.target.value)}
                                    required
                                >
                                    <option value="credit">Credit (+)</option>
                                    <option value="debit">Debit (-)</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="transaction-head">Account:</label>
                                <select
                                    value={account}
                                    className="input-field"
                                    onChange={handleAccountChange}
                                    required
                                >
                                    <option value="Savings">Savings</option>
                                    <option value="Investments">Investments</option>
                                    <option value="Loans">Loans</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="transaction-head">Amount ($):</label>
                                <input
                                    type="number"
                                    className="input-field"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    required
                                    min="0.01"
                                    step="0.01"
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="form-group">
                                <label className="transaction-head">Date:</label>
                                <input
                                    type="date"
                                    value={date}
                                    className="input-field"
                                    onChange={(e) => setDate(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="transaction-head">Description:</label>
                            <input
                                type="text"
                                value={description}
                                className="input-field"
                                placeholder="e.g., Bonus payment, Refund, etc."
                                onChange={(e) => setDescription(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            className="add-transaction-btn"
                            type="submit"
                            disabled={formSubmitting || !selectedUserId || (type === 'debit' && userBalance <= 0)}
                        >
                            {formSubmitting ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Processing...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-plus"></i> Add Transaction
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {successMessage && (
                    <div className="add-transaction-success">
                        <i className="fas fa-check-circle"></i>
                        <span>{successMessage}</span>
                        <Link to={`/userdetail/${selectedUserId}`} className="success-link">
                            View Transactions
                        </Link>
                    </div>
                )}

                {errorMessage && (
                    <div className="add-transaction-error">
                        <i className="fas fa-exclamation-circle"></i>
                        <span>{errorMessage}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddTransaction;